import { motion } from "framer-motion";
import { useState } from "react";
import { ExternalLink, Heart, MessageCircle, Send, Eye, Bookmark, Pencil } from "lucide-react";
import { PlatformIcon, linkPlatform } from "@/components/PlatformBadge";
import { CoverSetter } from "@/components/CoverSetter";
import { EditMetricsDialog } from "@/components/dialogs/EditMetricsDialog";
import { thumbUrl } from "@/lib/api";
import { fmtFull, timeAgo, fmtDate } from "@/lib/format";

// A real cover only when its source is trusted (self-hosted from the posting source, the post's
// og:image, or an owner-set manual cover). Stock/unsplash and un-backfilled covers never apply.
function hasRealCover(post) {
  const trusted = ["tiktok_oembed", "provider", "manual", "og"].includes(post.thumb_source);
  return Boolean(post.thumbnail) && trusted;
}

// SEC: only ever use http(s) URLs as an href (blocks javascript:/data: link injection).
function safeHref(url) {
  return /^https?:\/\//i.test(url || "") ? url : undefined;
}

// Never a "cover unavailable" tile: fall back to the artist photo, or the Playbook Distro logo
// when there's no artist photo. The real thumbnail swaps in automatically once a backfill lands.
function FallbackCover({ artistImage, className = "" }) {
  if (artistImage) {
    return (
      <img
        data-testid="post-cover-fallback"
        src={thumbUrl(artistImage, 480)}
        alt=""
        loading="lazy"
        decoding="async"
        className={`h-full w-full object-cover ${className}`}
      />
    );
  }
  return (
    <div data-testid="post-cover-fallback" className={`grid h-full w-full place-items-center bg-gradient-to-br from-[#0A3B36] to-[#35606E] ${className}`}>
      <img src="/favicon.png" alt="Playbook Distro" className="h-10 w-10 rounded-xl opacity-95" />
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between py-1.5 text-sm">
      <span className="flex items-center gap-2 text-neutral-500">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </span>
      <span className="num-display font-semibold text-[#111]">{value}</span>
    </div>
  );
}

export function PostCard({ post: initialPost, index = 0, view = "grid", editable = false, artistImage = "", dashZeros = false }) {
  const [post, setPost] = useState(initialPost);
  const [editOpen, setEditOpen] = useState(false);
  const onCover = (data) => setPost((p) => ({ ...p, thumbnail: data.thumbnail, thumb_source: data.thumb_source }));
  const handle = post.creator_handle?.replace(/^@/, "");
  const realCover = hasRealCover(post);
  const IG = post.platform === "instagram";
  const hidden = Boolean(post.counts_hidden);
  const unavail = post.stats_source === "unavailable";
  // Card-sourced (Creator Core) and manually-entered posts show their stored numbers verbatim:
  // a null metric is "—" (never 0), bypassing the platform-specific display heuristics below.
  const raw = post.stats_display === "raw";

  // Honest stat display — never show a fabricated number. Unavailable = "—" (null, not 0).
  const ma = post.metrics_available || {};
  const stat = (field) => {
    // Display-only, scoped to the Merges "Another State" surfaces: a stored numeric 0 renders as a
    // dash, matching G Network's zero rendering. Positive counts and nulls are unchanged.
    if (dashZeros && post[field] === 0) return "—";
    if (raw) return post[field] == null ? "—" : fmtFull(post[field]);
    if (ma[field] === false || post[field] === null || post[field] === undefined) {
      // explicit unavailable metric — but keep honest hidden/likes handling below for IG
      if (!(IG && field === "likes" && hidden)) return "—";
    }
    if (!IG) return fmtFull(post[field]);
    if (unavail) return "—";
    if (field === "comments") return fmtFull(post.comments);
    if (field === "shares" || field === "saves") return "—";
    if (field === "views") {
      if (hidden) return "Hidden";
      if (!post.is_video) return "—";
      return post.views == null ? "—" : fmtFull(post.views);
    }
    if (field === "likes") return hidden ? (post.likes > 0 ? fmtFull(post.likes) : "Hidden") : fmtFull(post.likes);
    return fmtFull(post[field]);
  };
  const erText = raw
    ? (post.engagement_rate == null ? "—" : `${post.engagement_rate}%`)
    : (post.engagement_rate == null || (IG && (unavail || hidden)) ? "—" : `${post.engagement_rate}%`);
  // Preserve the true source publication calendar date (date-only, never tz-shifted).
  const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const postedLabel = (() => {
    const sd = post.source_date;
    if (sd && /^\d{4}-\d{2}-\d{2}$/.test(sd)) {
      const [y, m, d] = sd.split("-").map((x) => parseInt(x, 10));
      return MON[m - 1] ? `${MON[m - 1]} ${d}, ${y}` : fmtDate(post.posted_at);
    }
    return fmtDate(post.posted_at);
  })();

  if (view === "list") {
    return (
      <>
      <motion.div
        data-testid={`post-row-${post.id}`}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: index * 0.03 }}
        className="pb-card flex flex-col gap-4 p-4 sm:flex-row sm:items-center"
      >
        <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-neutral-100">
          {realCover ? (
            <img src={thumbUrl(post.thumbnail, 160)} alt={handle} width={64} height={64} loading="lazy" decoding="async" className="h-full w-full object-cover" />
          ) : (
            <FallbackCover artistImage={artistImage} />
          )}
          <PlatformIcon platform={linkPlatform(post.post_url, post.platform)} className="absolute -bottom-1 -right-1 h-6 w-6" />
        </div>
        {editable && (
          <div className="relative z-10 shrink-0" data-testid={`post-cover-setter-${post.id}`}>
            <CoverSetter endpoint={`/posts/${post.id}/cover`} onDone={onCover} testid={`set-cover-${post.id}`} />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold text-[#111]">@{handle}</p>
          <p className="text-xs text-neutral-500">Posted {postedLabel}</p>
        </div>
        <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm sm:grid-cols-5">
          <Metric label="Views" value={stat("views")} />
          <Metric label="Likes" value={stat("likes")} />
          <Metric label="Comments" value={stat("comments")} />
          <Metric label="Shares" value={stat("shares")} />
          <Metric label="Eng. Rate" value={erText} />
        </div>
        <a
          data-testid={`post-link-${post.id}`}
          href={safeHref(post.post_url)}
          target="_blank"
          rel="noreferrer"
          className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-black/10 text-neutral-600 transition-colors hover:bg-black hover:text-white"
        >
          <ExternalLink className="h-4 w-4" />
        </a>
        {editable && (
          <button
            data-testid={`edit-metrics-${post.id}`}
            onClick={() => setEditOpen(true)}
            title="Edit metrics"
            className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-black/10 text-neutral-600 transition-colors hover:bg-[#35606E] hover:text-white"
          >
            <Pencil className="h-4 w-4" />
          </button>
        )}
      </motion.div>
      {editable && (
        <EditMetricsDialog open={editOpen} onOpenChange={setEditOpen} post={post} onDone={(doc) => setPost(doc)} />
      )}
      </>
    );
  }

  return (
    <>
    <motion.div
      data-testid={`post-card-${post.id}`}
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.04, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -4 }}
      className="pb-card group overflow-hidden p-0"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-neutral-100">
        {realCover ? (
          <img
            src={thumbUrl(post.thumbnail, 480)}
            alt={handle}
            width={480}
            height={360}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <FallbackCover artistImage={artistImage} />
        )}
        <PlatformIcon platform={linkPlatform(post.post_url, post.platform)} className="absolute left-3 top-3" />
        {editable && (
          <div className="absolute bottom-3 left-3 z-10" data-testid={`post-cover-setter-${post.id}`}>
            <CoverSetter endpoint={`/posts/${post.id}/cover`} onDone={onCover} testid={`set-cover-${post.id}`} />
          </div>
        )}
        <a
          data-testid={`post-link-${post.id}`}
          href={safeHref(post.post_url)}
          target="_blank"
          rel="noreferrer"
          className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-full bg-white/90 text-neutral-800 shadow-sm backdrop-blur transition-colors hover:bg-black hover:text-white"
        >
          <ExternalLink className="h-4 w-4" />
        </a>
        {editable && (
          <button
            data-testid={`edit-metrics-${post.id}`}
            onClick={() => setEditOpen(true)}
            title="Edit metrics"
            className="absolute right-12 top-3 grid h-8 w-8 place-items-center rounded-full bg-white/90 text-neutral-800 shadow-sm backdrop-blur transition-colors hover:bg-[#35606E] hover:text-white"
          >
            <Pencil className="h-4 w-4" />
          </button>
        )}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
          <p className="font-semibold text-white">@{handle}</p>
        </div>
      </div>
      <div className="px-4 pb-4 pt-2">
        <Row icon={Eye} label="Views" value={stat("views")} />
        <Row icon={Heart} label="Likes" value={stat("likes")} />
        <Row icon={MessageCircle} label="Comments" value={stat("comments")} />
        <Row icon={Send} label="Shares" value={stat("shares")} />
        <div className="my-1.5 h-px bg-black/[0.06]" />
        <div className="flex items-center justify-between py-1">
          <span className="flex items-center gap-2 text-sm text-neutral-500">
            <Bookmark className="h-3.5 w-3.5" /> Eng. Rate
          </span>
          <span className="num-display rounded-full bg-neutral-900 px-2.5 py-0.5 text-sm font-bold text-white">
            {erText}
          </span>
        </div>
        <div className="mt-2 flex items-center justify-between text-[11px] text-neutral-400">
          <span>Posted {postedLabel}</span>
          <span>Updated {timeAgo(post.last_updated)}</span>
        </div>
      </div>
    </motion.div>
    {editable && (
      <EditMetricsDialog open={editOpen} onOpenChange={setEditOpen} post={post} onDone={(doc) => setPost(doc)} />
    )}
    </>
  );
}

function Metric({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-neutral-400">{label}</p>
      <p className="num-display font-semibold text-[#111]">{value}</p>
    </div>
  );
}
